import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { bScreenMode, IUser } from '../../atoms';
import { useRecoilValue } from 'recoil';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../services/_common';
import { useThemeLogo } from '../../services/getInitialColorMode';
import Itlog from '../../assets/images/logo/itlog.png';
import SkeletonTable from '../SkeletonTable';
import IssueGuide from '../IssueGuide';
import i18n from '../../translation/i18n';
import useWeatherInfo from '../../hooks/useWeatherInfo';
import { CloudySvgIcon, SunnySvgIcon, LittleCloudySvgIcon, BlurSvgIcon, RainSvgIcon, ShowerSvgIcon, SleetSvgIcon, SnowSvgIcon, GoodSvgIcon, NormalSvgIcon, BadSvgIcon, VeryBadSvgIcon } from '../Icons';
import { t } from 'i18next';

const Root = styled.div`
  display: flex;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: space-between !important;
  color: var(--white);
  height: 100% !important;
  position: relative;

  &.is-pm {
    .site-name {
      width: 44%;
    }

    .time-weather {
      &.is-pm {
        > div {
          gap: 2.25rem;
        }
      }
    }
  }

  .logo-wrapper {
    position: absolute;
    top: 50%;
    left: 1.5rem;
    z-index: 10;
    transform: translateY(-50%);
    display: flex;
    align-items: center; /* 수직 중앙 정렬 */
    gap: 1rem; /* 이미지와 텍스트 사이 간격 추가 */
    width: fit-content;
    height: calc(100% - 3.5rem);

    > img {
      height: 100%;
      width: auto;
      object-fit: contain;
      object-position: center;
      flex-shrink: 0;
    }

    > div {
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: var(--text-primary);
      font-size: var(--widget-font-size-xl);
      display: flex;
      align-items: center;
      height: 100%;
    }
  }

  .site-name {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 10;
    width: 60%;
    transform: translate(-50%, -50%);
    font-weight: 600;
    font-size: var(--widget-font-size-4xl);
    color: var(--text-primary);
    line-height: 1.2;
    padding: 0.25rem 0;
    text-align: center;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .time-weather {
    position: absolute;
    top: 50%;
    right: 1.5rem;
    z-index: 10;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
    color: var(--text-primary);

    .time-weather-item-left {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;

      &.is-pm {
        .time-weather-item-right-time-wrapper {
          .ampm {
            font-weight: 400;
          }

          .ampm,
          .time {
            font-size: 32px;
          }
        }
      }

      .date {
        letter-spacing: 0.01rem;
        font-size: var(--widget-font-size-base);
      }

      .time-weather-item-right-time-wrapper {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;

        .ampm,
        .time {
          font-size: var(--widget-font-size-2xl);
          font-weight: 700;
        }
      }
    }

    .time-weather-item-right {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      .weather-icon {
        height: fit-content;
        > svg {
          width: 6rem;
        }
      }

      .weather-info-wrapper {
        gap: 0.75rem;

        .pm-info-wrapper {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;

          padding-left: 0.75rem;
          border-left: 1px solid #515151;

          .pm-info-item {
            display: flex;
            align-items: center;

            .pm-label {
              min-width: 50px;

              span {
                font-size: 16px;
              }
            }

            .pm-value {
              display: flex;
              align-items: center;
              gap: 0.375rem;

              b {
                font-weight: 500;
              }
            }
          }
        }
      }

      .weather-text-wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
        font-size: var(--widget-font-size-base);

        .weather-info-text {
          font-size: 18px;
        }

        .temp {
          font-size: var(--widget-font-size-2xl);
          font-weight: 700;
        }
      }

      > div {
        display: flex;
        align-items: center;
      }
    }

    > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 1.5rem;
      font-size: var(--widget-font-size-sm);
    }
  }
`;

// 시간 표시 컴포넌트
const TimeDisplay = memo(({ isPM }: { isPM: boolean }) => {
  const [date, setDate] = useState<string>('');
  const [currentTime, setCurrentTime] = useState<{ hours: string; minutes: string; ampm: string }>({
    hours: '',
    minutes: '',
    ampm: '',
  });

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = (now.getMonth() + 1).toString().padStart(2, '0');
      const day = now.getDate().toString().padStart(2, '0');
      const hours = now.getHours() % 12 || 12; // Convert 24-hour format to 12-hour format
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const ampm = now.getHours() >= 12 ? '오후' : '오전';

      const daysOfWeekKor = ['일', '월', '화', '수', '목', '금', '토'];
      const daysOfWeekEng = ['Sun', 'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat'];

      const dayOfWeek = i18n.language === 'ko-KR' ? daysOfWeekKor[now.getDay()] : daysOfWeekEng[now.getDay()];

      setDate(isPM ? `${year.toString().slice(2)}.${month}.${day} (${dayOfWeek})` : `${year}-${month}-${day} (${dayOfWeek})`);
      setCurrentTime({ hours: hours.toString(), minutes, ampm });
    };

    const intervalId = setInterval(() => {
      updateTime();
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className={`time-weather-item-left ${isPM ? 'is-pm' : ''}`}>
      <div className='date'>{date}</div>
      <div className='time-weather-item-right-time-wrapper'>
        <span className='ampm'>{currentTime.ampm}</span>
        <span className='time'>
          {currentTime.hours}:{currentTime.minutes}
        </span>
      </div>
    </div>
  );
});

TimeDisplay.displayName = 'TimeDisplay';

export const SiteDashboardTitle = ({ userInfo, type, isPM }: { userInfo: IUser; type: 's' | 'c'; isPM: boolean }) => {
  const colorMode = useRecoilValue(bScreenMode);
  const { hCd, sCd } = userInfo;
  const normalReqInfo = type === 's' ? { path: '/head/info', reqParam: { hCd } } : { path: '/company/info/one', reqParam: { cCd: hCd } };

  const headInfoQuery = useQuery(['headInfoGet', hCd], () => apiGet({ path: normalReqInfo.path, req: normalReqInfo.reqParam }));
  const theme = colorMode === '1' ? 'light' : 'dark';
  const logo = useThemeLogo(headInfoQuery, theme, type === 's' ? 'h' : 'c');
  const { tWeather, shortAdrs, openDayWeather, isErrorTimeWeather } = useWeatherInfo({ autoRefresh: true });
  // 로컬 스토리지에서 데이터 가져오기
  const currentWeatherLocalStorage = window.localStorage.getItem('currentWeatherW');
  const localCurrentWeather = currentWeatherLocalStorage //
    ? JSON.parse(currentWeatherLocalStorage)
    : { wsd: '', tmp: '', minTemp: '', maxTemp: '', rainPct: '', sky: '', pm10: '', pm25: '' };
  const [currentWeather, setCurrentWeather] = useState(localCurrentWeather);

  // 사이트 정보 쿼리 결과 처리
  useEffect(() => {
    if (shortAdrs) setCurrentWeather((prev: any) => ({ ...prev, district: shortAdrs }));
  }, [shortAdrs]);

  // 시간별 날씨 쿼리 결과 처리
  useEffect(() => {
    if (tWeather) {
      const { pm10, pm25 } = tWeather;
      setCurrentWeather((prev: any) => ({ ...prev, pm10, pm25 }));
    }
  }, [tWeather]);

  // 시간별 날씨 API 오류 시 오픈 날씨 API 대체
  useEffect(() => {
    if (!isErrorTimeWeather && openDayWeather) {
      const { sky, tmp } = openDayWeather;
      setCurrentWeather((prev: any) => ({ ...prev, sky, tmp }));
    }
  }, [isErrorTimeWeather, openDayWeather]);

  useEffect(() => {
    if (currentWeather) {
      window.localStorage.setItem('currentWeatherW', JSON.stringify(currentWeather));
    }
  }, [currentWeather]);

  const {
    data: siteInfoData,
    isLoading: isLoadingSiteInfoData,
    isError: isErrorSiteInfoData,
  } = useQuery(['siteInfoGet', hCd, sCd], () => (type === 's' ? fetchSiteInfo() : fetchCompanyInfo()), {
    enabled: type === 's' ? !!hCd && !!sCd && sCd !== '00000' : !!hCd && hCd.substring(0, 1).toLowerCase() === 'c',
  });

  const fetchSiteInfo = async () => {
    try {
      const res = await apiGet({ path: '/site/info', req: { hCd, sCd } });
      return res.data.data.siteInfo;
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const res = await apiGet({ path: '/company/info/one', req: { cCd: hCd } });
      return res.data.data[0];
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  const cWeather = useCallback((sky: string) => {
    const size = 80;
    switch (sky) {
      case 'sunny':
        return { svg: <SunnySvgIcon size={size} />, text: t('맑음') };
      case 'little_cloud':
        return { svg: <LittleCloudySvgIcon size={size} />, text: t('구름 적음') };
      case 'cloudy':
        return { svg: <LittleCloudySvgIcon size={size} />, text: t('구름 많음') };
      case 'blur':
        return { svg: <BlurSvgIcon size={size} />, text: t('흐림') };
      case 'rain':
        return { svg: <RainSvgIcon size={size} />, text: t('비') };
      case 'shower':
        return { svg: <ShowerSvgIcon size={size} />, text: t('소나기') };
      case 'snow':
        return { svg: <SnowSvgIcon size={size} />, text: t('눈') };
      case 'sleet':
        return { svg: <SleetSvgIcon size={size} />, text: t('진눈깨비') };
      default:
        return { svg: <SunnySvgIcon size={size} />, text: t('맑음') };
    }
  }, []);

  // 날씨 정보 메모이제이션
  const weatherInfo = useMemo(() => {
    return cWeather(currentWeather.sky);
  }, [currentWeather.sky, cWeather]);

  const pmStatus = {
    none: { svg: null, text: '-', color: 'var(--text-primary)' },
    good: { svg: <GoodSvgIcon size={20} color='var(--widget-status-blue)' />, text: t('좋음'), color: 'var(--widget-status-blue)' },
    normal: { svg: <NormalSvgIcon size={20} color='var(--widget-status-green)' />, text: t('보통'), color: 'var(--widget-status-green)' },
    bad: { svg: <BadSvgIcon size={20} color='var(--widget-status-yellow)' />, text: t('나쁨'), color: 'var(--widget-status-yellow)' },
    veryBad: { svg: <VeryBadSvgIcon size={20} color='var(--widget-status-red)' />, text: t('매우나쁨'), color: 'var(--widget-status-red)' },
  };

  // 미세먼지 컴포넌트
  const pm10StatusIcon = useCallback((value: number | string | null) => {
    // pm10 값이 없거나 숫자로 변환할 수 없는 경우('-' 등) '-' 처리, 숫자 문자열("35")은 숫자로 변환
    const status = Number(value);
    if (!status || Number.isNaN(status)) return pmStatus.none;

    if (status <= 30) return pmStatus.good;
    if (status > 30 && status <= 80) return pmStatus.normal;
    if (status > 80 && status <= 150) return pmStatus.bad;
    return pmStatus.veryBad; // 150 이상 매우나쁨
  }, []);

  // 미세먼지 정보 메모이제이션
  const pmInfo = useMemo(() => {
    return pm10StatusIcon(currentWeather.pm10);
  }, [currentWeather.pm10, pm10StatusIcon]);

  // 초미세먼지 컴포넌트
  const pm25StatusIcon = useCallback((value: number | string | null) => {
    // pm2.5 값이 없거나 숫자로 변환할 수 없는 경우('-' 등) '-' 처리, 숫자 문자열("35")은 숫자로 변환
    const status = Number(value);
    if (!status || Number.isNaN(status)) return pmStatus.none;

    if (status <= 15) return pmStatus.good;
    if (status > 15 && status <= 35) return pmStatus.normal;
    if (status > 35 && status <= 75) return pmStatus.bad;
    return pmStatus.veryBad; // 75 이상 매우나쁨
  }, []);

  // 초미세먼지 정보 메모이제이션
  const pm25Info = useMemo(() => {
    return pm25StatusIcon(currentWeather.pm25);
  }, [currentWeather.pm25, pm25StatusIcon]);

  const getSiteNameFontSize = (text: string) => {
    const len = text.length;
    if (len <= 16) return `${56}px`;
    const size = 54 - ((len - 16) / (35 - 16)) * (54 - 28);
    return `${Math.round(size)}px`;
  };

  if (headInfoQuery?.isError || isErrorSiteInfoData) return <IssueGuide isImg={false} />;
  if (headInfoQuery?.isLoading || isLoadingSiteInfoData) return <SkeletonTable rows={1} />;

  return (
    <Root className={`${isPM ? 'is-pm' : ''}`}>
      <div className='logo-wrapper'>
        <img src={logo !== '' ? `${logo}` : Itlog} alt='' />
        {/* <div>{headInfoQuery.data?.data?.data?.headInfo?.hName}</div> */}
      </div>
      <div className='site-name' style={{ fontSize: isPM ? getSiteNameFontSize(type === 's' ? siteInfoData?.sName : siteInfoData?.cName) : '56px' }}>
        {type === 's' ? siteInfoData?.sName : siteInfoData?.cName}
      </div>
      <div className={`time-weather ${isPM ? 'is-pm' : ''}`}>
        <div className='time-weather-item'>
          {!isPM && <TimeDisplay isPM={isPM} />}
          <div className='time-weather-item-right'>
            <div className='weather-icon'>{weatherInfo.svg}</div>

            {isPM ? (
              <div className='weather-info-wrapper'>
                <div className='weather-text-wrapper'>
                  <div className='weather-info-text'>{weatherInfo.text}</div>
                  <div className='temp'>{currentWeather.tmp}°</div>
                </div>
                <div className='pm-info-wrapper'>
                  <div className='pm-info-item'>
                    <div className='pm-label'>
                      <span className='label'>{t('미세')}</span>
                    </div>
                    <div className='pm-value' style={{ color: pmInfo.color }}>
                      {pmInfo.svg}
                      <b>
                        {pmInfo.text} {pmInfo.text === '-' ? '' : `(${currentWeather.pm10})`}
                      </b>
                    </div>
                  </div>
                  <div className='pm-info-item'>
                    <div className='pm-label'>
                      <span className='label'>{t('초미세')}</span>
                    </div>
                    <div className='pm-value' style={{ color: pm25Info.color }}>
                      {pm25Info.svg}
                      <b>
                        {pm25Info.text} {pm25Info.text === '-' ? '' : `(${currentWeather.pm25})`}
                      </b>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className='weather-text-wrapper'>
                <div>{weatherInfo.text}</div>
                <div className='temp'>{currentWeather.tmp}°</div>
              </div>
            )}
          </div>
          {isPM && <TimeDisplay isPM={isPM} />}
        </div>
      </div>
    </Root>
  );
};
